import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, MessageCircle, ChevronDown, ChevronUp, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { timeAgo } from "./common";
import { KolamAvatar } from "./KolamAvatar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { analytics } from "@/lib/analytics";

const SESSION = (() => {
  let id = localStorage.getItem("nc_session");
  if (!id) { id = Math.random().toString(36).slice(2); localStorage.setItem("nc_session", id); }
  return id;
})();

export function PostCard({ post, index, onChanged }) {
  const liked = JSON.parse(localStorage.getItem("nc_likes") || "{}")[post.id];
  const [likes, setLikes] = useState(post.likes || 0);
  const [isLiked, setIsLiked] = useState(!!liked);
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState([]);
  const [replyName, setReplyName] = useState("");
  const [replyText, setReplyText] = useState("");
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const author = post.author || post.name || "Anonymous";
  const content = post.content || post.body || "";
  const topic   = post.topic  || post.cat  || "General";

  const handleLike = async () => {
    try {
      const { data } = await api.post(`/posts/${post.id}/like`, { session_id: SESSION });
      setLikes(data.likes);
      setIsLiked(data.liked);
      if (data.liked) analytics.likePost(post.id);
      else analytics.unlikePost(post.id);
      const stored = JSON.parse(localStorage.getItem("nc_likes") || "{}");
      if (data.liked) stored[post.id] = true; else delete stored[post.id];
      localStorage.setItem("nc_likes", JSON.stringify(stored));
      onChanged?.();
    } catch { toast.error("Couldn't register like"); }
  };

  const toggleReplies = async () => {
    if (!showReplies) analytics.openReplies(post.id);
    if (!showReplies && replies.length === 0) {
      setLoadingReplies(true);
      try {
        const { data } = await api.get(`/posts/${post.id}/replies`);
        setReplies(data.replies || []);
      } catch { setReplies([]); } finally { setLoadingReplies(false); }
    }
    setShowReplies(v => !v);
  };

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/replies`, { post_id: post.id, name: replyName || "Anonymous", body: replyText });
      setReplies(r => [...r, data.reply]);
      setReplyText(""); setReplyName("");
      analytics.submitReply(post.id);
      toast.success("Reply posted!");
    } catch { toast.error("Couldn't post reply"); } finally { setSubmitting(false); }
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
      data-testid="post-card"
      className="rounded-2xl border border-border bg-card p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-center gap-3">
        <KolamAvatar name={author} city={post.city} size={36} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{author}</p>
          <p className="text-xs text-muted-foreground">{timeAgo(post.created_at)} {post.city ? `· ${post.city}` : ""}</p>
        </div>
        <span className="ml-auto shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">{topic}</span>
      </div>
      <div>
        <h3 className="font-semibold text-foreground text-sm leading-snug mb-1">{post.title}</h3>
        <p className="text-sm text-foreground/70 leading-relaxed line-clamp-3">{content}</p>
      </div>
      <div className="flex items-center gap-3 mt-auto pt-1 border-t border-border">
        <button onClick={handleLike} className={`flex items-center gap-1.5 text-xs transition-colors ${isLiked ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
          <Heart className={`w-3.5 h-3.5 ${isLiked ? "fill-primary" : ""}`} />{likes}
        </button>
        <button onClick={toggleReplies} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <MessageCircle className="w-3.5 h-3.5" />{post.replies || 0}
          {showReplies ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>
      {showReplies && (
        <div className="space-y-3 border-t border-border pt-3">
          {loadingReplies && <p className="text-xs text-muted-foreground">Loading…</p>}
          {replies.map(r => (
            <div key={r.id} className="flex gap-2">
              <KolamAvatar name={r.name} city={post.city} size={28} />
              <div><p className="text-xs font-medium text-foreground">{r.name} <span className="text-muted-foreground font-normal">{r.time}</span></p><p className="text-xs text-foreground/80 mt-0.5">{r.body}</p></div>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Input placeholder="Your name" value={replyName} onChange={e => setReplyName(e.target.value)} className="h-8 text-xs w-28 shrink-0" />
            <Input placeholder="Write a reply…" value={replyText} onChange={e => setReplyText(e.target.value)} className="h-8 text-xs" onKeyDown={e => e.key === "Enter" && submitReply()} />
            <Button size="icon" className="h-8 w-8 shrink-0" onClick={submitReply} disabled={submitting}><Send className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}
    </motion.article>
  );
}
